import { jsx } from 'react/jsx-runtime';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { deepmerge } from '@mui/utils';
import { THEME_PREFIX } from './types.js';
import { produce } from 'immer';
import { isWhite } from '../utils/stringUtils.js';
import brandclub_colors from '../colors/brandclubColors.js';

const processTheme = (themeOptions) => {
    if (!themeOptions)
        return themeOptions;
    const sanitizedThemeOptions = produce(themeOptions, (draft) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var _j, _k;
        draft !== null && draft !== void 0 ? draft : (draft = {});
        // Remove customCss from the draft since it's not a valid Material-UI theme property
        delete draft.customCss;
        /**
         * Recursively updates keys whose value corresponds to any URL.
         * a plain URL string causes the theme cssvars to fail to load which breaks the theme.
         * we wrap the URL in a css url() function to prevent the theme cssvars from failing to load.
         */
        const processThemeUrls = (obj) => {
            Object.keys(obj).forEach((key) => {
                const value = obj[key];
                if (typeof value === 'string' && value.match(/https?:\/\//)) {
                    obj[key] = `url(${value})`;
                    return;
                }
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    processThemeUrls((value !== null && value !== void 0 ? value : {}));
                }
            });
        };
        processThemeUrls(draft);
        // Fix typography lineHeight to prevent malformed font shorthand CSS variables
        if (draft.typography && typeof draft.typography === 'object') {
            const typography = draft.typography;
            Object.keys(typography).forEach((key) => {
                const variant = typography[key];
                if (variant && typeof variant === 'object' && variant.lineHeight === 'inherit') {
                    // Change 'inherit' to 'normal' to prevent malformed font shorthand
                    variant.lineHeight = 'normal';
                }
            });
        }
        let backdropColor = brandclub_colors.black;
        if (((_b = (_a = themeOptions.palette) === null || _a === void 0 ? void 0 : _a.text) === null || _b === void 0 ? void 0 : _b.primary) && !isWhite((_d = (_c = themeOptions.palette) === null || _c === void 0 ? void 0 : _c.text) === null || _d === void 0 ? void 0 : _d.primary)) {
            backdropColor = themeOptions.palette.text.primary;
        }
        (_e = draft.components) !== null && _e !== void 0 ? _e : (draft.components = {});
        (_f = (_j = draft.components).MuiBackdrop) !== null && _f !== void 0 ? _f : (_j.MuiBackdrop = {});
        (_g = (_k = draft.components.MuiBackdrop).styleOverrides) !== null && _g !== void 0 ? _g : (_k.styleOverrides = {});
        draft.components.MuiBackdrop.styleOverrides.root = {
            backgroundColor: `rgba(${(_h = backdropColor
                .replace('#', '')
                .match(/.{1,2}/g)) === null || _h === void 0 ? void 0 : _h.map((hex) => parseInt(hex, 16)).join(', ')}, 0.21)`,
            transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            zIndex: 900,
        };
    });
    return sanitizedThemeOptions;
};
const UpdateCustomThemeContext = createContext(null);
const CustomThemeOptionsContext = createContext(undefined);
const useUpdateCustomTheme = () => {
    const _updateCustomTheme = useContext(UpdateCustomThemeContext);
    const customThemeOptions = useContext(CustomThemeOptionsContext);
    if (!_updateCustomTheme || !customThemeOptions) {
        throw new Error('useUpdateCustomTheme must be used within a CustomThemeProvider');
    }
    const updateCustomTheme = useCallback((update, forceUpdate = false) => {
        if (forceUpdate) {
            return _updateCustomTheme(update);
        }
        return _updateCustomTheme((prev) => deepmerge(prev, typeof update === 'function' ? update(prev) : update, { clone: true }));
    }, [_updateCustomTheme, customThemeOptions]);
    return [customThemeOptions, updateCustomTheme];
};
const WithUpdateCustomThemeOptions = (props) => {
    const [themeOptions, setThemeOptions] = useState(props.themeOptions);
    useEffect(() => {
        setThemeOptions(props.themeOptions);
    }, [props.themeOptions]);
    return (jsx(UpdateCustomThemeContext.Provider, Object.assign({ value: setThemeOptions }, { children: jsx(CustomThemeOptionsContext.Provider, Object.assign({ value: themeOptions }, { children: props.children })) })));
};
const CustomThemeProviderConsumer = (props) => {
    const [themeOptions] = useUpdateCustomTheme();
    const theme = createTheme(Object.assign(Object.assign({}, processTheme(themeOptions)), { cssVariables: {
            cssVarPrefix: THEME_PREFIX,
        } }));
    return jsx(ThemeProvider, Object.assign({ theme: theme }, { children: props.children }));
};
const CustomThemeProvider = (props) => {
    return (jsx(WithUpdateCustomThemeOptions, Object.assign({ themeOptions: props.themeOptions }, { children: jsx(CustomThemeProviderConsumer, { children: props.children }) })));
};

export { CustomThemeProvider, useUpdateCustomTheme };
//# sourceMappingURL=customTheme.js.map
